/** From the stored finance model to the engine's input. The server and the screens both use this. */
import { yearEndOf } from "./dates";
import type { CommitmentInput, ForecastInput, LeverInput } from "./engine";
import { coveredUntil, sourceLines } from "./sources";
import type { Commitment, FinanceModel, Lever } from "./types";

/** "applied": every lever applied to the forecast. "approved": only the applied levers that are approved. */
export type ForecastView = "applied" | "approved";

/** A lever counts in the forecast when it's applied (never once not approved), and in the approved view only once approved. */
export function inForecast(l: Pick<Lever, "applied" | "status">, view: ForecastView): boolean {
  if (!l.applied || l.status === "not_approved") return false;
  return view === "applied" || l.status === "approved";
}

export const toCommitmentInput = (c: Commitment, after?: string | null): CommitmentInput => ({
  id: c.id,
  direction: c.direction,
  basis: c.basis,
  schedule: c.schedule.map((s) => ({ date: s.date, repeat: s.repeat, until: s.until, amount: s.amount })),
  after: after ?? null,
});

export const toLeverInput = (l: Pick<Lever, "id" | "entries">): LeverInput => ({
  id: l.id,
  entries: l.entries.map((e) => ({ date: e.date, repeat: e.repeat, until: e.until, amount: e.amount, commitmentId: e.commitmentId, basis: e.basis })),
});

/** The engine's input for a view, or null until a bank balance has been entered. */
export function engineInput(model: FinanceModel, view: ForecastView): ForecastInput | null {
  if (!model.balance) return null;
  return {
    asOf: model.balance.asAt,
    opening: model.balance.amount,
    commitments: commitmentInputs(model, model.balance.asAt),
    levers: model.levers.filter((l) => inForecast(l, view)).map(toLeverInput),
    settings: {
      yearEnd: yearEndOf(model.balance.asAt, model.settings.yearStartMonth),
      gateWeeks: model.settings.gateWeeks,
      runOutWeeks: model.settings.runOutWeeks,
      thresholds: { green: model.thresholds.green, amber: model.thresholds.amber, red: model.thresholds.red },
    },
  };
}

/**
 * Every line the forecast counts: those entered here (each linked one only
 * after the latest Xero document that covers it), and what Xero and the Sales
 * Hub said. A Xero contact's documents join the line linked to it.
 */
export function commitmentInputs(model: FinanceModel, asOf: string): CommitmentInput[] {
  const covered = coveredUntil(model);
  const entered = model.commitments.filter((c) => c.active).map((c) => toCommitmentInput(c, covered.get(c.id)));
  const synced = sourceLines(model, asOf).map(
    (l): CommitmentInput => ({ id: l.linkedTo ?? l.id, direction: l.direction, basis: l.basis, origin: l.origin, schedule: l.rows.map(({ label: _, ...r }) => r) }),
  );
  return [...entered, ...synced];
}
